/**
 * lrb-personalization.js — v3 Embed + Signal Edition
 * Leaps & Rebounds Customer Intelligence Snippet
 *
 * Architecture:
 *   - Sections live inside inert <template> elements in #lrb-template-pool
 *   - First ABOVE_FOLD_COUNT sections are cloned into #lrb-intelligence-inject immediately
 *   - Remaining sections use IntersectionObserver sentinels to lazy-inject 500px ahead of scroll
 *   - Zero DOM parsing cost for any section until it's actually needed
 *
 * v3 additions:
 *   - embed_id support: pass via window.LRB_INTELLIGENCE_CONFIG.embedId
 *   - UTM signal auto-detection: reads URL params, fires segment_tags to profile
 *   - first_time_user / returning signals via localStorage TTL
 *
 * DEV MODE: verbose console logging + on-page debug overlay.
 * Flip DEV = false before production.
 */

(function () {
  'use strict';

  // ── Run-once guard ───────────────────────────────────────────────────────────
  // Prevents double-execution if lrb-embed.liquid is accidentally added to the
  // Shopify template more than once (causes duplicate sections on the page).
  if (window.__lrbPersonalizationLoaded) {
    console.warn('[LRB] lrb-personalization.js loaded twice — aborting duplicate instance.');
    return;
  }
  window.__lrbPersonalizationLoaded = true;

  var DEBUG_PANEL = (new URLSearchParams(window.location.search)).get('lrb_debug') === '1';
  var DEV = DEBUG_PANEL; // enables console logs + section outlines when ?lrb_debug=1


  // ── Config ──────────────────────────────────────────────────────────────────

  var CONFIG = window.LRB_INTELLIGENCE_CONFIG || {};
  var BOT_URL = CONFIG.botUrl || 'https://gravity-claw-production-fb9e.up.railway.app';
  var EMBED_ID = CONFIG.embedId || null; // null = return all active sections

  var COOKIE_NAME      = 'lrb_vid';
  var COOKIE_DAYS      = 365;
  var HOST_ID          = 'lrb-intelligence-inject';   // mount point
  var POOL_ID          = 'lrb-template-pool';          // inert template pool
  var ABOVE_FOLD_COUNT = 2;   // sections injected immediately on load
  var MAX_SECTIONS     = 50;  // effectively unlimited — show all as user scrolls
  var LOOK_AHEAD_PX    = 600; // px below viewport to trigger next injection
  var SECTION_CACHE_KEY = 'lrb_ranked_' + (EMBED_ID || 'default') + '_' + window.location.pathname;
  var CACHE_TTL_MS     = 10 * 60 * 1000; // 10 minutes
  var API_TIMEOUT_MS   = 8000; // 8s — gives Railway time even behind Puppeteer's request intercept
  var FIRST_SEEN_KEY   = 'lrb_first_seen'; // localStorage — ms timestamp of first visit
  var FIRST_USER_TTL   = 24 * 60 * 60 * 1000; // 24h

  // ── Session-level impressed sections ────────────────────────────────────────
  // Accumulates every sectionId that receives an impression this page session.
  // The document-level ATC listener uses this list to attribute conversions
  // to all sections the visitor actually saw — since the Shopify native ATC
  // button lives outside our injected section elements.
  var _impressedSections = [];
  var _atcListenerAttached = false;
  var _atcDebounceTimer = null;
  var _atcFired = false;

  // Hoisted to IIFE top-level so both installFetchIntercept (runs immediately)
  // and attachTracking listeners can call it. Strict mode makes function
  // declarations inside blocks block-scoped, so this MUST be at the top level.
  function _fireAtcForAllSections() {
    if (_atcFired) return; // debounce — one report per ATC action
    _atcFired = true;
    clearTimeout(_atcDebounceTimer);
    _atcDebounceTimer = setTimeout(function() { _atcFired = false; }, 600);
    var ids = _impressedSections.slice();
    if (!ids.length) return; // no sections impressed yet — nothing to attribute
    log('\uD83D\uDED2 ATC detected \u2014 attributing to', ids.length, 'impressed section(s):', ids);
    dbg('\uD83D\uDED2 ATC \u2192 ' + ids.join(', '));
    ids.forEach(function(id) { reportEvent('add_to_cart', id); });
  }

  // ── fetch() intercept — page-level ATC capture ───────────────────────────────
  // Intercepts every /cart/add.js fetch — the ONE network call all Shopify
  // themes make for ATC regardless of form submit, ajax, or custom events.
  (function installFetchIntercept() {
    try {
      var _origFetch = window.fetch;
      window.fetch = function(input, init) {
        var url = typeof input === 'string' ? input
                : (input && input.url) ? input.url : '';
        var result = _origFetch.apply(this, arguments);
        if (url.indexOf('/cart/add') !== -1) {
          result.then(function(response) {
            if (response.ok) _fireAtcForAllSections();
          }).catch(function() { /* noop */ });
        }
        return result;
      };
    } catch(e) { /* fetch not patchable — form/event listeners remain as fallback */ }
  })();

  // ── Preconnect hint ──────────────────────────────────────────────────────────

  (function injectPreconnect() {
    try {
      var host = new URL(BOT_URL).origin;
      if (!document.querySelector('link[rel="preconnect"][href="' + host + '"]')) {
        var pc = document.createElement('link');
        pc.rel = 'preconnect'; pc.href = host; pc.crossOrigin = 'anonymous';
        document.head.appendChild(pc);
        var dns = document.createElement('link');
        dns.rel = 'dns-prefetch'; dns.href = host;
        document.head.appendChild(dns);
      }
    } catch(e) { /* noop */ }
  })();

  // ── Dev helpers ─────────────────────────────────────────────────────────────

  function log() {
    if (!DEV) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[LRB 🧠]');
    console.log.apply(console, args);
  }

  function err() {
    if (!DEV) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[LRB ❌]');
    console.error.apply(console, args);
  }

  var overlay;
  function dbg(msg) {
    if (!DEV) return;
    log(msg);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'lrb-debug-overlay';
      overlay.style.cssText = [
        'position:fixed', 'bottom:12px', 'right:12px', 'z-index:99999',
        'background:rgba(0,0,0,0.85)', 'color:#4ade80', 'font-family:monospace',
        'font-size:11px', 'padding:10px 14px', 'border-radius:8px',
        'max-width:380px', 'max-height:280px', 'overflow-y:auto',
        'border:1px solid rgba(74,222,128,0.3)', 'line-height:1.6',
        'pointer-events:none',
      ].join(';');
      document.body.appendChild(overlay);
    }
    var line = document.createElement('div');
    line.textContent = '▸ ' + msg;
    overlay.appendChild(line);
    overlay.scrollTop = overlay.scrollHeight;
  }

  // ── Cookie helpers ───────────────────────────────────────────────────────────

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  // ── Visitor ID ───────────────────────────────────────────────────────────────

  var visitorId = getCookie(COOKIE_NAME);
  if (!visitorId) {
    visitorId = uuid();
    setCookie(COOKIE_NAME, visitorId, COOKIE_DAYS);
    log('New visitor ID created:', visitorId);
  } else {
    log('Returning visitor ID:', visitorId);
  }

  // ── Signal extraction ────────────────────────────────────────────────────────

  function getPageSignals() {
    var params = new URLSearchParams(window.location.search);
    var signals = {
      page_type: (document.body.className.match(/template-(\w+)/) || [])[1] || 'unknown',
      referrer: document.referrer ? (function(){ try { return new URL(document.referrer).hostname; } catch(e){ return document.referrer; } })() : '',
      url: window.location.pathname,
    };
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
      if (params.get(k)) signals[k] = params.get(k);
    });
    return signals;
  }

  function getAdSignals() {
    var params = new URLSearchParams(window.location.search);
    var ad = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'fbclid', 'gclid'].forEach(function (k) {
      if (params.get(k)) ad[k] = params.get(k);
    });
    return Object.keys(ad).length ? ad : null;
  }

  // ── Auto-signal detection ────────────────────────────────────────────────────
  // Derives segment_tags from URL params & localStorage state.
  // These are merged into the profile upsert — no separate API call.

  function deriveAutoSignals() {
    var tags = [];
    var params = new URLSearchParams(window.location.search);

    // ── UTM-based signals ────────────────────────────────────────────────────
    // Marketing team encodes intent directly in UTM campaign values
    var campaign = (params.get('utm_campaign') || '').toLowerCase();
    var source   = (params.get('utm_source')   || '').toLowerCase();

    if (campaign.indexOf('knee') !== -1 || campaign.indexOf('joint') !== -1) tags.push('knee_pain');
    if (campaign.indexOf('back') !== -1) tags.push('back_pain');
    if (campaign.indexOf('shoulder') !== -1) tags.push('shoulder_pain');
    if (campaign.indexOf('senior') !== -1 || campaign.indexOf('50') !== -1) tags.push('senior');
    if (campaign.indexOf('athletic') !== -1 || campaign.indexOf('performance') !== -1) tags.push('athlete');
    if (campaign.indexOf('wellness') !== -1 || campaign.indexOf('pain_relief') !== -1) tags.push('pain_relief');
    if (source === 'facebook' || source === 'instagram' || params.get('fbclid')) tags.push('facebook_ad');
    if (source === 'google' || params.get('gclid')) tags.push('google_ad');

    // ── Page-view signals ────────────────────────────────────────────────────
    var pathname = window.location.pathname.toLowerCase();
    if (pathname.indexOf('/blogs/joint') !== -1 || pathname.indexOf('/blogs/knee') !== -1) tags.push('pain_relief');
    if (pathname.indexOf('/blogs/performance') !== -1 || pathname.indexOf('/blogs/hiit') !== -1) tags.push('athlete');
    if (pathname.indexOf('/blogs/senior') !== -1 || pathname.indexOf('/blogs/mobility') !== -1) tags.push('senior');

    // ── Time-based signals ───────────────────────────────────────────────────
    var now = Date.now();
    try {
      var firstSeen = localStorage.getItem(FIRST_SEEN_KEY);
      if (!firstSeen) {
        // Brand new visitor — set first_seen
        localStorage.setItem(FIRST_SEEN_KEY, String(now));
        tags.push('first_time_user');
        log('Signal: first_time_user (new)');
        dbg('Signal: first_time_user');
      } else {
        var age = now - parseInt(firstSeen, 10);
        if (age < FIRST_USER_TTL) {
          // Still within 24h window
          tags.push('first_time_user');
          log('Signal: first_time_user (still within 24h, age:', Math.round(age / 60000) + 'min)');
        } else {
          // Expired — they're a returning visitor
          tags.push('returning');
          log('Signal: returning (first seen', Math.round(age / 86400000) + 'd ago)');
          dbg('Signal: returning');
        }
      }
    } catch(e) { /* localStorage blocked */ }

    if (tags.length) {
      log('Auto-signals derived:', tags);
      dbg('Signals: ' + tags.join(', '));
    }
    return tags.length ? [...new Set(tags)] : null;
  }

  // ── Fire-and-forget profile upsert ───────────────────────────────────────────

  function upsertProfile(extra) {
    var send = function() {
      var autoTags = deriveAutoSignals();
      var payload = Object.assign({ visitor_id: visitorId, page_signals: getPageSignals() }, extra || {});
      var adSignals = getAdSignals();
      if (adSignals) payload.ad_signals = adSignals;
      // Attach auto-derived segment_tags (merged server-side with existing tags)
      if (autoTags) payload.segment_tags = autoTags;

      log('Profile upsert →', BOT_URL + '/admin/intelligence/profile', payload);
      fetch(BOT_URL + '/admin/intelligence/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      })
        .then(function(r) { return r.json(); })
        .then(function(d) { log('Profile upsert ok:', d); })
        .catch(function(e) { err('Profile upsert failed:', e.message); });
      dbg('Profile signal sent (' + payload.page_signals.page_type + ')');
    };
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(send, { timeout: 2000 });
    } else {
      setTimeout(send, 300);
    }
  }

  // ── Event reporting ──────────────────────────────────────────────────────────

  function reportEvent(eventType, sectionId, extra) {
    var payload = { visitor_id: visitorId, section_shopify_id: sectionId || null, event_type: eventType };
    if (extra) Object.assign(payload, extra);
    log('Event:', eventType, sectionId || '', extra || '');
    fetch(BOT_URL + '/admin/intelligence/page/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(function(e) { err('Event report failed:', e.message); });
  }

  // ── Inline script re-execution ──────────────────────────────────────────────

  function reExecScripts(wrapper) {
    wrapper.querySelectorAll('script').forEach(function(orig) {
      var s = document.createElement('script');
      Array.prototype.slice.call(orig.attributes).forEach(function(a) {
        s.setAttribute(a.name, a.value);
      });
      s.textContent = orig.textContent;
      orig.parentNode.replaceChild(s, orig);
    });
  }

  // ── Section entrance animations ──────────────────────────────────────────────
  // Injected once; two variants:
  //   lrb-reveal-immediate — above-fold (fast, subtle — section was always "there")
  //   lrb-reveal-lazy      — below-fold (slightly slower + more travel — arriving on scroll)

  var _animStylesInjected = false;
  function injectAnimationStyles() {
    if (_animStylesInjected) return;
    _animStylesInjected = true;
    var s = document.createElement('style');
    s.textContent = [
      '@keyframes lrb-fade-up-fast{',
        'from{opacity:0;transform:translateY(16px)}',
        'to{opacity:1;transform:translateY(0)}',
      '}',
      '@keyframes lrb-fade-up-lazy{',
        'from{opacity:0;transform:translateY(28px)}',
        'to{opacity:1;transform:translateY(0)}',
      '}',
      '.lrb-reveal-immediate{',
        'animation:lrb-fade-up-fast 0.42s cubic-bezier(0.22,1,0.36,1) both;',
      '}',
      '.lrb-reveal-lazy{',
        'animation:lrb-fade-up-lazy 0.54s cubic-bezier(0.22,1,0.36,1) both;',
      '}',
      // Respect reduced-motion preference
      '@media(prefers-reduced-motion:reduce){',
        '.lrb-reveal-immediate,.lrb-reveal-lazy{',
          'animation:none;opacity:1;transform:none;',
        '}',
      '}',
    ].join('');
    document.head.appendChild(s);
  }

  // ── Template cloning ─────────────────────────────────────────────────────────

  function cloneTemplate(pool, sectionId) {
    var tmpl = pool.querySelector('template[data-lrb-id="' + sectionId + '"]');
    if (!tmpl) {
      err('Template not found for section:', sectionId, '— deploy the snippet first');
      dbg('No template for: ' + sectionId);
      return null;
    }
    var wrapper = document.createElement('div');
    wrapper.setAttribute('data-lrb-id', sectionId);
    wrapper.appendChild(tmpl.content.cloneNode(true));
    reExecScripts(wrapper);
    return wrapper;
  }

  // ── Tracking attachment ───────────────────────────────────────────────────────

  function attachTracking(el, sectionId) {
    if (DEV) {
      el.style.outline = '2px dashed rgba(74,222,128,0.35)';
      el.style.outlineOffset = '-2px';
    }

    // Record impression
    reportEvent('impression', sectionId);

    // Track for session-level ATC attribution — push only if not already present
    if (_impressedSections.indexOf(sectionId) === -1) {
      _impressedSections.push(sectionId);
    }

    // Attach the document-level ATC listener exactly once per page load.
    // Shopify's native ATC button ([name="add"]) lives in main-product.liquid,
    // completely outside our injected section elements — per-element listeners
    // would never fire for it. One document listener catches all paths:
    //   - Standard form submit (name="add" button click)
    //   - Theme JS that dispatches a custom "cart:add" or "theme:cart:add" event
    //   - Shopify ajax cart (catches fetch/XHR to /cart/add.js via the custom event
    //     Shopify themes fire on window after a successful add)
    if (!_atcListenerAttached) {
      _atcListenerAttached = true;

      // 1. Native form submit
      document.addEventListener('submit', function(e) {
        var form = e.target;
        if (!form || form.nodeName !== 'FORM') return;
        if ((form.action || '').indexOf('/cart/add') !== -1) _fireAtcForAllSections();
      }, { passive: true, capture: true });

      // 2. Custom events fired by modern Shopify themes after ajax ATC
      ['cart:item:added','cart:add','theme:cart:add','ajax:cart:add','product:added'].forEach(function(evName) {
        window.addEventListener(evName, _fireAtcForAllSections, { passive: true });
        document.addEventListener(evName, _fireAtcForAllSections, { passive: true });
      });

      // 3. Button click fallback
      document.addEventListener('click', function(e) {
        if (e.target && e.target.closest && e.target.closest('[name="add"], .btn-add-to-cart, [data-action="add-to-cart"], [data-add-to-cart]')) {
          _fireAtcForAllSections();
        }
      }, { passive: true });
    }

    // Dwell tracking — measures time section is ≥50% in viewport
    var startTime = null;
    var dwellObs = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          startTime = startTime || Date.now();
        } else if (startTime) {
          var ms = Date.now() - startTime;
          if (ms > 500) reportEvent('dwell', sectionId, { dwell_ms: ms });
          startTime = null;
        }
      });
    }, { threshold: 0.5 });
    dwellObs.observe(el);

    // Click tracking — only fires on meaningful CTA interactions, NOT internal
    // carousel controls, tabs, accordions, or image clicks. Elements that should
    // NOT trigger a CTR count must have data-lrb-internal attribute.
    el.addEventListener('click', function(e) {
      var target = e.target;
      if (!target || !target.closest) return;
      // Skip elements explicitly marked as internal controls
      if (target.closest('[data-lrb-internal]')) return;
      // Only count clicks on outbound-intent elements
      var isCta = target.closest(
        'a[href]:not([href="#"]):not([href=""]), ' +
        'button:not([data-lrb-internal]), ' +
        '[data-lrb-cta]'
      );
      if (isCta) reportEvent('click', sectionId);
    }, { passive: true });
  }



  // ── Immediate injection ───────────────────────────────────────────────────────

  function injectNow(host, pool, sectionId) {
    if (host.querySelector('[data-lrb-id="' + sectionId + '"]')) {
      log('Already injected, skipping:', sectionId);
      return;
    }
    var el = cloneTemplate(pool, sectionId);
    if (!el) return;
    injectAnimationStyles();
    el.classList.add('lrb-reveal-immediate');
    host.appendChild(el);
    attachTracking(el, sectionId);
    log('⚡ Injected immediately:', sectionId);
    dbg('⚡ ' + sectionId);
  }

  // ── Sentinel-based lazy injection ────────────────────────────────────────────

  function setupLazyInjection(host, pool, sectionIds) {
    if (!sectionIds.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var sentinel = entry.target;
        var sectionId = sentinel.getAttribute('data-lrb-sentinel');

        log('👁 Sentinel triggered for:', sectionId);
        dbg('Lazy inject: ' + sectionId);

        var el = cloneTemplate(pool, sectionId);
        if (el) {
          injectAnimationStyles();
          el.classList.add('lrb-reveal-lazy');
          host.replaceChild(el, sentinel);
          attachTracking(el, sectionId);
          log('✅ Lazy-injected:', sectionId);
        } else {
          host.removeChild(sentinel);
        }

        observer.unobserve(sentinel);
      });
    }, {
      rootMargin: '0px 0px ' + LOOK_AHEAD_PX + 'px 0px',
      threshold: 0,
    });

    sectionIds.forEach(function(sectionId) {
      var sentinel = document.createElement('div');
      sentinel.setAttribute('data-lrb-sentinel', sectionId);
      sentinel.style.cssText = 'height:200px;width:100%;';
      host.appendChild(sentinel);
      observer.observe(sentinel);
      log('📍 Sentinel placed for:', sectionId);
    });
  }

  // ── Cold-start fallback injection ─────────────────────────────────────────────
  // Called when the /page/sections API fails or times out (e.g. Railway cold-start).
  // Injects the first ABOVE_FOLD_COUNT templates from the pool alphabetically so the
  // page is never left blank — far better than an empty personalization zone.
  // Once the visitor's next page load hits a warm Railway instance the real ranked
  // sections take over (sessionStorage cache will be empty for this session).

  function injectFallback(host, pool) {
    // Only run if nothing has already been injected
    if (host.querySelector('[data-lrb-id]')) return;

    // Prefer user-configured fallback list, otherwise use the pool order
    var fallbackIds = (CONFIG.fallbackSections && CONFIG.fallbackSections.length)
      ? CONFIG.fallbackSections.slice(0, ABOVE_FOLD_COUNT)
      : (function() {
          var tmpls = pool.querySelectorAll('template[data-lrb-id]');
          var ids = [];
          for (var i = 0; i < Math.min(ABOVE_FOLD_COUNT, tmpls.length); i++) {
            ids.push(tmpls[i].getAttribute('data-lrb-id'));
          }
          return ids;
        })();

    if (!fallbackIds.length) return;

    log('⚠️ API failed — injecting', fallbackIds.length, 'fallback section(s):', fallbackIds);
    dbg('⚠️ Fallback: ' + fallbackIds.join(', '));

    fallbackIds.forEach(function(id) {
      injectNow(host, pool, id);
    });
  }

  // ── Main ─────────────────────────────────────────────────────────────────────

  function run() {
    log('=== LRB Personalization v3 (embed + signal edition) ===');
    log('BOT_URL:', BOT_URL);
    log('Visitor ID:', visitorId);
    if (EMBED_ID) log('Embed ID:', EMBED_ID);

    var host = document.getElementById(HOST_ID);
    var pool = document.getElementById(POOL_ID);

    if (!host) {
      err('No #' + HOST_ID + ' found — section will not inject.');
      dbg('ERROR: #' + HOST_ID + ' missing');
      upsertProfile();
      return;
    }
    if (!pool) {
      err('No #' + POOL_ID + ' found — templates missing.');
      dbg('ERROR: #' + POOL_ID + ' missing');
      upsertProfile();
      return;
    }

    var templateCount = pool.querySelectorAll('template[data-lrb-id]').length;
    log('Templates in pool:', templateCount);
    dbg('Template pool: ' + templateCount + ' snippets ready');

    // Step 1: fire profile upsert (idle — non-blocking, includes auto-signals)
    upsertProfile();

    // Step 2: get ranked sections (with sessionStorage cache)
    var sectionsUrl = BOT_URL + '/admin/intelligence/page/sections';

    var cachedRaw = null;
    try { cachedRaw = sessionStorage.getItem(SECTION_CACHE_KEY); } catch(e) {}
    if (cachedRaw) {
      try {
        var envelope = JSON.parse(cachedRaw);
        var age = Date.now() - (envelope.ts || 0);
        if (age < CACHE_TTL_MS && envelope.data) {
          log('⚡ Using sessionStorage cache (' + Math.round(age / 1000) + 's old):', envelope.data.sections.length, 'sections');
          dbg('⚡ Cache hit (' + Math.round(age / 1000) + 's old) — skipping API');
          applyRankedSections(host, pool, envelope.data);
          // ── Fire impression RPCs for UCB1 accuracy on cache hits ─────────────
          // POST /page/sections is skipped on cache hits, so the server-side
          // increment_embed_impressions / increment_variation_impressions RPCs
          // never fire. Ping the lightweight impression endpoint so UCB1 stats
          // stay accurate even for multi-page sessions within the cache window.
          if (EMBED_ID && envelope.data.sections && envelope.data.sections.length) {
            var _sids = envelope.data.sections.map(function(s) { return s.section_id; }).filter(Boolean);
            var _vids = envelope.data.sections.map(function(s) { return s.variation_id; }).filter(Boolean);
            if (_sids.length) {
              fetch(BOT_URL + '/admin/intelligence/page/sections/impression', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ embed_id: EMBED_ID, section_ids: _sids, variation_ids: _vids }),
                keepalive: true,
              }).catch(function() { /* non-critical — best-effort */ });
              log('📊 Cache-hit impression ping sent for', _sids.length, 'sections');
              dbg('📊 Cache impression ping → ' + _sids.length + ' sections');
            }
          }
          return;
        } else {
          log('Cache expired (' + Math.round(age / 1000) + 's old) — fetching fresh');
          dbg('Cache expired — fetching fresh rankings');
          try { sessionStorage.removeItem(SECTION_CACHE_KEY); } catch(e2) {}
        }
      } catch(e) {
        try { sessionStorage.removeItem(SECTION_CACHE_KEY); } catch(e2) {}
      }
    }

    log('Requesting ranked sections from', sectionsUrl);

    var body = { visitor_id: visitorId };
    if (EMBED_ID) body.embed_id = EMBED_ID;

    fetch(sectionsUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(API_TIMEOUT_MS),
    })
      .then(function(r) {
        log('Sections API:', r.status);
        return r.ok ? r.json() : { sections: [] };
      })
      .then(function(data) {
        try { sessionStorage.setItem(SECTION_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data })); } catch(e) {}
        applyRankedSections(host, pool, data);
      })
      .catch(function(e) {
        err('Sections API failed:', e.message);
        dbg('Sections API failed: ' + e.message);
        // ── Fallback: inject first ABOVE_FOLD_COUNT templates from the pool so the page
        // is never blank on a Railway cold-start or network error.
        injectFallback(host, pool);
      });
  }

  // ── Apply ranked sections ─────────────────────────────────────────────────────

  function applyRankedSections(host, pool, data) {
    var ranked = (data.sections || []).slice(0, MAX_SECTIONS);

    // Filter out sections with no shopify_section_id (misconfigured rows)
    ranked = ranked.filter(function(s) { return s.shopify_section_id; });

    // Deduplicate by shopify_section_id — stale cache can produce repeated entries
    var seen = {};
    ranked = ranked.filter(function(s) {
      if (seen[s.shopify_section_id]) return false;
      seen[s.shopify_section_id] = true;
      return true;
    });

    log('Ranked sections:', ranked.length, ranked.map(function(s){
      return s.shopify_section_id + (s.variation_name ? ' [' + s.variation_name + ']' : '');
    }));
    dbg('Ranked: ' + ranked.map(function(s){ return s.shopify_section_id; }).join(', '));

    if (!ranked.length) {
      dbg('No ranked sections — falling back to pool defaults');
      log('⚠️ No sections returned — calling injectFallback');
      injectFallback(host, pool);
      return;
    }

    var aboveFold = ranked.slice(0, ABOVE_FOLD_COUNT);
    var belowFold = ranked.slice(ABOVE_FOLD_COUNT);

    log('Above fold:', aboveFold.map(function(s){ return s.shopify_section_id; }));
    log('Below fold (lazy):', belowFold.map(function(s){ return s.shopify_section_id; }));
    dbg('Above fold: ' + aboveFold.length + '  |  Lazy: ' + belowFold.length);

    // Inject using shopify_section_id — matches template[data-lrb-id] in the pool
    aboveFold.forEach(function(s) {
      injectNow(host, pool, s.shopify_section_id);
    });

    if (belowFold.length) {
      setupLazyInjection(host, pool, belowFold.map(function(s){ return s.shopify_section_id; }));
    }

    dbg('Done — ' + aboveFold.length + ' immediate, ' + belowFold.length + ' lazy');

    var maxPct = 0;
    window.addEventListener('scroll', function() {
      var pct = Math.round((window.scrollY + window.innerHeight) / document.body.scrollHeight * 100);
      if (pct > maxPct) maxPct = pct;
    }, { passive: true });

    window.addEventListener('beforeunload', function() {
      if (maxPct > 10) reportEvent('scroll_depth', null, { scroll_pct: maxPct });
    });
  }




  // ── QA Signal Debug Panel ─────────────────────────────────────────────────
  // Enabled via ?lrb_debug=1 in the URL.
  // Lets you pick signals, apply them as a profile override, and re-render sections.

  function buildDebugPanel() {
    if (!DEBUG_PANEL) return;

    var SIGNALS = {
      pain: [
        { key: 'knee',     label: '🦵 Knee Pain' },
        { key: 'back',     label: '🔙 Back Pain' },
        { key: 'shoulder', label: '💪 Shoulder' },
        { key: 'joint',    label: '🦴 Joint Pain' },
        { key: 'hip',      label: '🦴 Hip Pain' },
      ],
      life_stage: [
        { key: 'senior',       label: '👴 Senior (50+)' },
        { key: 'athlete',      label: '🏃 Athlete' },
        { key: 'post_injury',  label: '🩹 Post Injury' },
        { key: 'parent',       label: '👨‍👧 Parent' },
        { key: 'general',      label: '🙍 General' },
      ],
      motivation: [
        { key: 'performance', label: '⚡ Performance' },
        { key: 'fitness',     label: '💪 Fitness' },
        { key: 'wellness',    label: '🌿 Wellness' },
        { key: 'recovery',    label: '🧘 Recovery' },
        { key: 'weight_loss', label: '⚖️ Weight Loss' },
      ],
      tags: [
        { key: 'first_time_user', label: '👋 First Visit' },
        { key: 'returning',       label: '🔄 Returning' },
        { key: 'cart_abandoner',  label: '🛒 Cart Abandon' },
        { key: 'purchased',       label: '✅ Purchased' },
        { key: 'knee_pain',       label: '🦵 knee_pain' },
        { key: 'pain_relief',     label: '💊 pain_relief' },
        { key: 'facebook_ad',     label: '📘 facebook_ad' },
        { key: 'google_ad',       label: '🔍 google_ad' },
      ],
    };

    // Panel state
    var state = { pain: {}, life_stage: null, motivation: null, tags: {} };

    // Inject styles
    var style = document.createElement('style');
    style.textContent = [
      '#lrb-qa-panel{position:fixed;bottom:16px;left:16px;z-index:99998;',
      'width:290px;background:rgba(10,12,20,0.96);border:1px solid rgba(56,189,248,0.25);',
      'border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.5);font-family:monospace;',
      'font-size:12px;color:#e2e8f0;overflow:hidden;transition:height 0.2s;}',

      '#lrb-qa-header{display:flex;align-items:center;justify-content:space-between;',
      'padding:10px 14px;background:rgba(56,189,248,0.1);border-bottom:1px solid rgba(56,189,248,0.15);cursor:pointer;}',
      '#lrb-qa-header span{font-weight:700;color:#38bdf8;letter-spacing:0.04em;font-size:11px;}',
      '#lrb-qa-toggle{background:none;border:none;color:#475569;cursor:pointer;font-size:14px;line-height:1;}',

      '#lrb-qa-body{padding:12px 14px;max-height:420px;overflow-y:auto;}',
      '.lrb-qa-group{margin-bottom:12px;}',
      '.lrb-qa-group-label{font-size:9px;text-transform:uppercase;letter-spacing:0.1em;',
      'color:#64748b;font-weight:700;margin-bottom:6px;display:block;}',
      '.lrb-qa-chips{display:flex;flex-wrap:wrap;gap:4px;}',
      '.lrb-qa-chip{padding:3px 8px;border-radius:20px;border:1px solid rgba(255,255,255,0.1);',
      'background:rgba(255,255,255,0.04);color:#94a3b8;cursor:pointer;font-size:10px;',
      'transition:all 0.15s;user-select:none;}',
      '.lrb-qa-chip:hover{border-color:rgba(56,189,248,0.4);color:#38bdf8;}',
      '.lrb-qa-chip.active{background:rgba(56,189,248,0.15);border-color:#38bdf8;color:#38bdf8;font-weight:700;}',

      '#lrb-qa-actions{display:flex;gap:6px;margin-top:10px;}',
      '#lrb-qa-apply{flex:1;padding:7px;border-radius:7px;border:none;',
      'background:rgba(56,189,248,0.2);color:#38bdf8;font-weight:700;font-size:11px;',
      'cursor:pointer;letter-spacing:0.04em;transition:background 0.15s;}',
      '#lrb-qa-apply:hover{background:rgba(56,189,248,0.35);}',
      '#lrb-qa-reset{padding:7px 10px;border-radius:7px;border:1px solid rgba(255,255,255,0.08);',
      'background:none;color:#475569;font-size:11px;cursor:pointer;}',
      '#lrb-qa-result{margin-top:10px;font-size:10px;color:#64748b;line-height:1.6;',
      'border-top:1px solid rgba(255,255,255,0.05);padding-top:8px;}',
      '#lrb-qa-result b{color:#34d399;}',
      '#lrb-qa-cache-reset{width:100%;margin-top:8px;padding:6px;border-radius:7px;',
      'border:1px solid rgba(245,158,11,0.25);background:rgba(245,158,11,0.08);',
      'color:#f59e0b;font-size:10px;font-weight:700;cursor:pointer;letter-spacing:0.03em;',
      'font-family:monospace;transition:all 0.15s;}',
      '#lrb-qa-cache-reset:hover{background:rgba(245,158,11,0.18);}',
    ].join('');
    document.head.appendChild(style);

    // Build panel HTML
    var panel = document.createElement('div');
    panel.id = 'lrb-qa-panel';

    var header = document.createElement('div');
    header.id = 'lrb-qa-header';
    header.innerHTML = '<span>🧪 LRB Signal Tester</span><button id="lrb-qa-toggle" aria-label="Toggle panel">▼</button>';
    panel.appendChild(header);

    var body = document.createElement('div');
    body.id = 'lrb-qa-body';

    // Build chip groups
    var groupDefs = [
      { id: 'pain',       label: 'Pain Signals',   multi: true,  items: SIGNALS.pain },
      { id: 'life_stage', label: 'Life Stage',      multi: false, items: SIGNALS.life_stage },
      { id: 'motivation', label: 'Motivation',      multi: false, items: SIGNALS.motivation },
      { id: 'tags',       label: 'Behavior Tags',   multi: true,  items: SIGNALS.tags },
    ];

    groupDefs.forEach(function(grp) {
      var wrap = document.createElement('div');
      wrap.className = 'lrb-qa-group';
      var lbl = document.createElement('span');
      lbl.className = 'lrb-qa-group-label';
      lbl.textContent = grp.label;
      wrap.appendChild(lbl);
      var chips = document.createElement('div');
      chips.className = 'lrb-qa-chips';
      grp.items.forEach(function(item) {
        var chip = document.createElement('button');
        chip.className = 'lrb-qa-chip';
        chip.type = 'button';
        chip.textContent = item.label;
        chip.dataset.key = item.key;
        chip.dataset.group = grp.id;
        chip.addEventListener('click', function() {
          if (grp.multi) {
            // Toggle multi
            if (grp.id === 'pain') state.pain[item.key] = state.pain[item.key] ? 0 : 0.9;
            if (grp.id === 'tags') state.tags[item.key] = !state.tags[item.key];
            chip.classList.toggle('active');
          } else {
            // Radio within group
            chips.querySelectorAll('.lrb-qa-chip').forEach(function(c) { c.classList.remove('active'); });
            chip.classList.add('active');
            if (grp.id === 'life_stage') state.life_stage = item.key;
            if (grp.id === 'motivation') state.motivation = item.key;
          }
        });
        chips.appendChild(chip);
      });
      wrap.appendChild(chips);
      body.appendChild(wrap);
    });

    // Actions
    var actions = document.createElement('div');
    actions.id = 'lrb-qa-actions';
    actions.innerHTML = '<button id="lrb-qa-apply" type="button">▶ Apply &amp; Re-render</button>' +
                        '<button id="lrb-qa-reset" type="button">Reset</button>';
    body.appendChild(actions);

    // Schema cache reset button
    var cacheBtn = document.createElement('button');
    cacheBtn.id = 'lrb-qa-cache-reset';
    cacheBtn.type = 'button';
    cacheBtn.textContent = '🔄 Reset Schema Cache (fix section-add issues)';
    body.appendChild(cacheBtn);

    var result = document.createElement('div');
    result.id = 'lrb-qa-result';
    result.style.display = 'none';
    body.appendChild(result);

    panel.appendChild(body);
    document.body.appendChild(panel);

    // Toggle collapse
    var collapsed = false;
    header.addEventListener('click', function() {
      collapsed = !collapsed;
      body.style.display = collapsed ? 'none' : '';
      document.getElementById('lrb-qa-toggle').textContent = collapsed ? '▲' : '▼';
    });

    // Apply button
    document.getElementById('lrb-qa-apply').addEventListener('click', function() {
      var btn = this;
      btn.textContent = '⏳ Applying…';
      btn.disabled = true;

      // Build profile payload
      var painMap = {};
      Object.keys(state.pain).forEach(function(k) { if (state.pain[k] > 0) painMap[k] = state.pain[k]; });

      var activeTags = Object.keys(state.tags).filter(function(k) { return state.tags[k]; });

      var extra = {};
      if (Object.keys(painMap).length) extra.pain_points = painMap;
      if (state.life_stage) extra.life_stage = state.life_stage;
      if (state.motivation) extra.motivation_type = state.motivation;
      if (activeTags.length) extra.segment_tags = activeTags;

      var payload = Object.assign({ visitor_id: visitorId, page_signals: getPageSignals() }, extra);

      log('[QA Panel] Applying profile override:', payload);

      // Upsert profile with forced signals
      fetch(BOT_URL + '/admin/intelligence/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function(r) { return r.json(); })
        .then(function() {
          // Bust the section cache so a fresh API call fires
          try { sessionStorage.removeItem(SECTION_CACHE_KEY); } catch(e) {}

          // Re-fetch ranked sections with inline profile (bypasses DB lookup)
          var body2 = { visitor_id: visitorId, embed_id: EMBED_ID, profile: extra };
          return fetch(BOT_URL + '/admin/intelligence/page/sections', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body2),
          });
        })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var ranked = (data.sections || []);
          var host = document.getElementById(HOST_ID);

          // Clear existing injected sections
          if (host) {
            var existing = host.querySelectorAll('[data-lrb-id]');
            existing.forEach(function(el) { host.removeChild(el); });

            var pool = document.getElementById(POOL_ID);
            ranked.slice(0, MAX_SECTIONS).forEach(function(s) {
              injectNow(host, pool, s.shopify_section_id || s.section_id);
            });
          }

          // Show result
          result.style.display = 'block';
          result.innerHTML = '<b>' + ranked.length + ' section(s) rendered:</b><br>' +
            ranked.map(function(s) {
              return '• ' + (s.section_name || s.section_id) +
                (s.variation_name ? ' <span style="color:#7dd3fc">(' + s.variation_name + ')</span>' : '') +
                ' <span style="color:#64748b">score:' + (s.score || 0).toFixed(2) + '</span>';
            }).join('<br>');

          btn.textContent = '✓ Applied';
          setTimeout(function() { btn.textContent = '▶ Apply & Re-render'; btn.disabled = false; }, 2000);
        })
        .catch(function(e) {
          result.style.display = 'block';
          result.innerHTML = '<span style="color:#f43f5e">Error: ' + e.message + '</span>';
          btn.textContent = '▶ Apply & Re-render'; btn.disabled = false;
        });
    });

    // Reset button
    document.getElementById('lrb-qa-reset').addEventListener('click', function() {
      state = { pain: {}, life_stage: null, motivation: null, tags: {} };
      panel.querySelectorAll('.lrb-qa-chip').forEach(function(c) { c.classList.remove('active'); });
      result.style.display = 'none';
      try { sessionStorage.removeItem(SECTION_CACHE_KEY); } catch(e) {}
      log('[QA Panel] State reset');
    });

    // Schema cache reset button
    document.getElementById('lrb-qa-cache-reset').addEventListener('click', function() {
      var btn = this;
      btn.textContent = '⏳ Reloading schema…';
      btn.disabled = true;
      fetch(BOT_URL + '/admin/db/reload-schema', { method: 'POST' })
        .then(function(r) { return r.json(); })
        .then(function(d) {
          if (d.ok) {
            btn.textContent = '✅ Schema cache reloaded!';
            btn.style.borderColor = 'rgba(52,211,153,0.4)';
            btn.style.background = 'rgba(52,211,153,0.1)';
            btn.style.color = '#34d399';
          } else {
            btn.textContent = '⚠️ ' + (d.error || 'Failed — check Railway logs');
            btn.style.color = '#f43f5e';
          }
          setTimeout(function() {
            btn.textContent = '🔄 Reset Schema Cache (fix section-add issues)';
            btn.style = '';
            btn.disabled = false;
          }, 4000);
        })
        .catch(function(e) {
          btn.textContent = '❌ ' + e.message;
          btn.style.color = '#f43f5e';
          setTimeout(function() {
            btn.textContent = '🔄 Reset Schema Cache (fix section-add issues)';
            btn.style = '';
            btn.disabled = false;
          }, 3000);
        });
    });

    log('[QA Panel] Mounted — signal tester active');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Mount debug panel after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildDebugPanel);
  } else {
    buildDebugPanel();
  }

})();
